import { Link } from 'react-router-dom';

/**
 * HomeHero — banner inmersivo premium del landing de Mi Boleta (`/`).
 *
 * Rediseño (solo estético): banner tipo marketplace de eventos, con una
 * imagen REAL de público en concierto (`heroImage`, tomada del catálogo real —
 * la portada más representativa) tratada A SANGRE, difuminada y **fundida** con
 * el fondo mediante:
 *   - blur + baja opacidad sobre el canvas oscuro (`#0a0f1f`);
 *   - duotono azul→violeta→cian en `mix-blend-mode: overlay` (paleta del hero);
 *   - scrim de legibilidad (oscuro a la izquierda, donde va el texto, y hacia
 *     abajo, para fundir con el resto del Home);
 *   - glows radiales + grid técnica.
 * La imagen NO es una tarjeta superpuesta: cubre todo el hero y se feathera con
 * gradientes, quedando como una escena, no como una pieza pegada.
 *
 * Copy, CTAs y destinos son los mismos del Home:
 *   - CTA primaria  → `/eventos`      ("Explorar eventos")
 *   - CTA secundaria → `/experiences` ("Ver experiencias VIP")
 */

function CheckIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function ArrowIcon(): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

// Señales de confianza derivadas del copy real del Home (no se inventan
// claims nuevos): "Compra segura", "tu QR al instante", "todo en un solo lugar".
const TRUST_POINTS = ['Compra 100% segura', 'Tu QR al instante', 'Todo en un solo lugar'] as const;

interface HomeHeroProps {
  /** Imagen de fondo del hero (dato real: portada representativa del catálogo). */
  heroImage?: string | null;
}

export function HomeHero({ heroImage = null }: HomeHeroProps): JSX.Element {
  return (
    <section className="relative isolate flex min-h-[540px] items-center overflow-hidden bg-[#0a0f1f] text-white lg:min-h-[600px]">
      {/* Backdrop — imagen real de concierto, a sangre, difuminada y fundida. */}
      {heroImage && (
        <img
          src={heroImage}
          alt=""
          aria-hidden="true"
          loading="eager"
          decoding="async"
          className="pointer-events-none absolute inset-0 -z-20 h-full w-full scale-105 object-cover object-[center_35%] opacity-[0.55]"
          style={{ filter: 'blur(2px) saturate(1.15)' }}
        />
      )}

      {/* Duotono azul→violeta→cian sobre la foto (fusión con la paleta). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'linear-gradient(120deg, rgba(0,118,244,0.48) 0%, rgba(124,58,237,0.44) 58%, rgba(0,214,238,0.30) 100%)',
          mixBlendMode: 'overlay',
        }}
      />

      {/* Scrim de legibilidad — oscuro a la izquierda (texto) y hacia abajo. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'linear-gradient(90deg, rgba(10,15,31,0.96) 0%, rgba(10,15,31,0.82) 34%, rgba(10,15,31,0.44) 64%, rgba(10,15,31,0.58) 100%),' +
            'linear-gradient(180deg, rgba(10,15,31,0.55) 0%, transparent 24%, transparent 60%, rgba(10,15,31,0.94) 100%)',
        }}
      />

      {/* Glows radiales (azul Mi Boleta → violeta → cian). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(50% 80% at 6% 2%, rgba(0,118,244,0.42) 0%, transparent 60%),' +
            'radial-gradient(55% 80% at 99% 16%, rgba(124,58,237,0.40) 0%, transparent 62%),' +
            'radial-gradient(60% 80% at 84% 114%, rgba(0,214,238,0.20) 0%, transparent 60%)',
        }}
      />

      {/* Grid técnica muy sutil para textura. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.12]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.12) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgba(255,255,255,0.12) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(120% 100% at 28% 0%, #000 18%, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(120% 100% at 28% 0%, #000 18%, transparent 78%)',
        }}
      />

      <div className="container-fc relative w-full py-16 md:py-20 lg:py-24">
        <div className="max-w-2xl">
          <p className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-white/80 backdrop-blur">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-cyan opacity-75 motion-safe:animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan" />
            </span>
            Mi Boleta
          </p>

          <h1 className="mt-5 font-display text-4xl font-bold leading-[1.03] tracking-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.45)] sm:text-5xl md:text-6xl lg:text-7xl">
            Viví los mejores eventos y experiencias,{' '}
            <span className="bg-gradient-to-r from-cyan via-white to-cyan bg-clip-text text-transparent">
              sin fricción.
            </span>
          </h1>

          <p className="mt-5 max-w-xl text-base text-white/80 md:text-lg">
            Boletas, palcos, experiencias VIP y beneficios de membresía — todo en un solo lugar.
            Compra segura y tu QR al instante.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Link
              to="/eventos"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-card bg-white px-6 text-base font-semibold text-primary-700 shadow-lg shadow-primary-900/30 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-neutral-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Explorar eventos
              <ArrowIcon />
            </Link>
            <Link
              to="/experiences"
              className="inline-flex h-12 items-center justify-center gap-2 rounded-card border border-white/25 bg-white/10 px-6 text-base font-semibold text-white backdrop-blur transition-colors duration-200 hover:bg-white/20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Ver experiencias VIP
            </Link>
          </div>

          <ul className="mt-9 flex flex-wrap gap-x-6 gap-y-3">
            {TRUST_POINTS.map((point) => (
              <li key={point} className="inline-flex items-center gap-2 text-sm text-white/85">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-cyan/15 text-cyan">
                  <CheckIcon />
                </span>
                {point}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Borde inferior suave hacia el canvas claro del resto del Home. */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"
      />
    </section>
  );
}
